import React from "react";
//#region types
import { CurrencyEnums } from "models/enums";
import { Amount } from "models/utilTypes/currencyType";
import { WintSuperkollAPIModel as ApiModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { IconDefinitionConstants } from "utils/constants";
import { CommonEnums } from "models/enums";
import { SupplierListProps } from "models/propTypes/supplierPropTypes";
import { OrganizationInfo } from "models/propTypes/userProfilePropTypes";
import { TranslationKeys } from "translations";
import { SupplierFormTypes } from "models/formTypes";
import { TableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Icon, TableAsync, useFilterContext, useSearchContext } from "components/common";
import { useNavigate, useLocation } from "react-router-dom";
import { useRatingService, useSupplierService } from "serviceHooks";
//#endregion
import { CSSHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const SupplierList: React.FC<SupplierListProps> = (props) => {
  const ratingService = useRatingService();
  const supplierService = useSupplierService();
  const location = useLocation();
  const navigate = useNavigate();
  const { filter } = useFilterContext<SupplierFormTypes.Filter>();
  const searchContext = useSearchContext();

  const filterFunc = React.useCallback(
    (pagination: WintUtilsWebApi.Filter) =>
      supplierService.getSuppliers(pagination, {
        ...(filter ?? {}),
        search: searchContext?.searchText,
      }),
    [filter, searchContext?.searchText]
  );

  const onRowClick = (row: ApiModel.IncomingInvoiceSupplierInfo) => {
    navigate(UrlHelper.formatPath(`/Supplier/${row.id}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  const getCountText = (count?: number): string => {
    if (count) return count + " " + LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Pc);
    else return "";
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars --- will be re-added with updated implementation
  const getRatingClass = (rating: number): string => {
    const ratingData = ratingService.getRatingInfo(CommonEnums.OrganizationTypes.Supplier, rating);
    return CSSHelper.getTagClass(ratingData.displayType);
  };

  const tableProperties: Partial<
    Record<keyof TableRowCommonKeys | keyof ApiModel.IncomingInvoiceSupplierInfo, TableRowProperties>
  > = {
    name: {
      description: TranslationKeys.Common.Name,
      helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.HelpTextName),
      formatter: (r: ApiModel.IncomingInvoiceSupplierInfo) => new OrganizationInfo(r.id, r.name ?? "", r.orgNr),
      className: cssExports.iconProfileContainerFullHeight,
    },
    // rating: {
    //   description: TranslationKeys.Common.Rating,
    //   helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.HelpTextRating),
    //   formatter: (r: ApiModel.IncomingInvoiceSupplierInfo) => (
    //     // <Tags tags={[r.rating.toString()]} className={getRatingClass(r.rating)}></Tags>
    //   ),
    // },
    numOfRules: {
      description: <Icon icon={IconDefinitionConstants.WBolt} />,
      helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.HelpTextRules),
      formatter: (r: ApiModel.IncomingInvoiceSupplierInfo) => getCountText(r.numOfRules),
      alignment: "right",
      classification: "count",
    },
    numOfInvoices: {
      description: TranslationKeys.Invoice.NumOfInvoices,
      helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.HelpTextNumOfInvoices),
      formatter: (r: ApiModel.IncomingInvoiceSupplierInfo) => getCountText(r.numOfInvoices),
      alignment: "right",
      classification: "count",
    },
    totalInvoicedAmount: {
      description: TranslationKeys.Common.Purchase,
      helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.HelpTextInvoicedTotal),
      formatter: (r: ApiModel.IncomingInvoiceSupplierInfo) =>
        new Amount(CurrencyEnums.CurrencyCode.SEK, r.totalInvoicedAmount),
      alignment: "right",
    },
    toPay: {
      description: TranslationKeys.Common.Unpaid2,
      helpText: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Supplier.HelpTextDue),
      formatter: (r: ApiModel.IncomingInvoiceSupplierInfo) => new Amount(CurrencyEnums.CurrencyCode.SEK, r.toPay),
      alignment: "right",
      className: cssExports.textBlack,
      classification: "amount",
    },
  };

  return (
    <TableAsync<ApiModel.IncomingInvoiceSupplierInfo>
      properties={tableProperties}
      filter={filterFunc}
      pagination={"advanced"}
      maxRowCount={10}
      rowClickHandler={onRowClick}
      orderByProperty="totalInvoicedAmount"
      orderByDescending
    />
  );
};
export default SupplierList;
